import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  TablePagination,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface SaleOrder {
  id: number;
  name: string;
  partner_id: [number, string];
  date_order: string;
  amount_total: number;
  state: string;
  invoice_name?: string | null;
}

interface SaleOrderDetail {
  id: number;
  name: string;
  partner_id?: number;
  partner_name: string;
  partner_street?: string;
  date_order: string;
  amount_total: number;
  amount_untaxed: number;
  amount_tax: number;
  state: string;
  invoice_name?: string | null;
  order_lines: Array<{
    product_id: number;
    product_name: string;
    product_qty: number;
    price_unit: number;
    price_subtotal: number;
    uom_name: string;
  }>;
}

const SaleOrderList: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<SaleOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<SaleOrderDetail | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchOrders(0, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchOrders = async (pageNum: number = page, size: number = rowsPerPage) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/odoo/sale_orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: pageNum, page_size: size }
      });
      setOrders(response.data.orders);
      setTotalCount(response.data.total_count);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cargar los pedidos');
    } finally {
      setLoading(false);
    }
  };

  const handleOrderClick = (order: SaleOrder) => {
    // Si es borrador, redirigir a edición
    if (order.state === 'draft') {
      navigate(`/edit-order/${order.id}`);
    } else {
      // Si está confirmado, mostrar detalle
      fetchOrderDetail(order.id);
    }
  };

  const fetchOrderDetail = async (orderId: number) => {
    try {
      const response = await axios.get(`${API_URL}/odoo/sale_order/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedOrder(response.data.order);
      setDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cargar el detalle del pedido');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedOrder(null);
  };

  const handleDownloadPdf = async (orderId: number) => {
    try {
      setDownloadingPdf(true);
      const response = await axios.get(`${API_URL}/odoo/sale_order/${orderId}/invoice/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob', // Importante para descargar archivos binarios
      });

      // Extraer el nombre del archivo del header Content-Disposition
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Factura-${selectedOrder?.name}.pdf`; // Fallback por defecto

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].trim();
        }
      }

      // Crear un enlace temporal para descargar el archivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Error al descargar el PDF. Asegúrate de que el pedido tenga una factura confirmada.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder) return;

    try {
      setCancellingOrder(true);
      await axios.post(
        `${API_URL}/odoo/sale_order/${selectedOrder.id}/cancel`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setShowCancelModal(false);
      handleCloseDialog();
      setSuccessMessage('Pedido cancelado exitosamente. Se ha creado la Nota de Crédito y devuelto el stock.');
      fetchOrders(); // Recargar la lista

      // Ocultar mensaje después de 5 segundos
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cancelar el pedido.');
    } finally {
      setCancellingOrder(false);
    }
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case 'draft':
        return 'default';
      case 'sale':
        return 'success';
      case 'done':
        return 'primary';
      case 'cancel':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStateLabel = (state: string) => {
    switch (state) {
      case 'draft':
        return 'Borrador';
      case 'sale':
        return 'Confirmado';
      case 'done':
        return 'Completado';
      case 'cancel':
        return 'Cancelado';
      default:
        return state;
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchOrders(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    fetchOrders(0, newSize);
  };

  // Ya paginado desde el servidor
  const paginatedOrders = orders;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 1, sm: 3 } }}>
      <Paper elevation={3} sx={{ padding: { xs: 2, sm: 3, md: 4 }, borderRadius: { xs: 0, sm: 2 } }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
            fontWeight: 600,
            color: 'primary.main',
            mb: 3
          }}
        >
          Lista de Pedidos de Venta
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}

        {orders.length === 0 ? (
          <Alert severity="info">No hay pedidos registrados</Alert>
        ) : (
          <TableContainer sx={{ overflowX: 'auto', borderRadius: 1 }}>
            <Table sx={{ minWidth: { xs: 300, sm: 650 } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                    #
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, color: 'white', fontWeight: 600 }}>
                    Número
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Cliente</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, color: 'white', fontWeight: 600 }}>
                    Fecha
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Total</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, color: 'white', fontWeight: 600 }}>
                    Estado
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedOrders.map((order) => (
                  <TableRow
                    key={order.id}
                    hover
                    sx={{
                      bgcolor: order.state === 'cancel' ? 'error.lighter' : 'inherit',
                      '&:hover': { bgcolor: order.state === 'cancel' ? 'error.light' : 'action.hover' },
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <TableCell sx={{
                      fontWeight: 600,
                      color: order.state === 'cancel' ? 'error.main' : 'primary.main',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {order.id}
                        {order.state === 'cancel' && (
                          <Chip
                            label="✕"
                            size="small"
                            color="error"
                            sx={{
                              height: '18px',
                              fontSize: '0.7rem',
                              display: { xs: 'inline-flex', sm: 'none' }
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontWeight: 500 }}>
                      {order.name}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      maxWidth: { xs: '120px', sm: '200px', md: '300px' },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {order.partner_id[1]}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {new Date(order.date_order.replace(' ', 'T') + 'Z').toLocaleDateString('es-ES')}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600,
                        color: 'success.main'
                      }}
                    >
                      S/ {order.amount_total.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Chip
                        label={getStateLabel(order.state)}
                        color={getStateColor(order.state)}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ padding: { xs: '4px', sm: '16px' } }}>
                      <Tooltip title={order.state === 'draft' ? 'Editar' : 'Ver detalle'}>
                        <IconButton
                          size="small"
                          color={order.state === 'draft' ? 'warning' : 'primary'}
                          onClick={() => handleOrderClick(order)}
                          sx={{
                            border: '1px solid',
                            borderColor: order.state === 'draft' ? 'warning.main' : 'primary.main',
                            padding: { xs: '6px', sm: '8px' }
                          }}
                        >
                          {order.state === 'draft' ? <EditIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </TableContainer>
        )}
      </Paper>

      {/* Dialog for Order Detail */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, py: 2 }}>
          Detalle del Pedido: {selectedOrder?.name}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedOrder && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, minHeight: '400px' }}>
              {/* Columna Izquierda - Información General */}
              <Box sx={{ p: 3, bgcolor: 'action.hover', borderRight: { md: '1px solid' }, borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}>
                  Información General
                </Typography>

                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                      Cliente
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedOrder.partner_name}
                    </Typography>
                    {selectedOrder.partner_street && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {selectedOrder.partner_street}
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                      Fecha del Pedido
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {new Date(selectedOrder.date_order.replace(' ', 'T') + 'Z').toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                      Estado
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={getStateLabel(selectedOrder.state)}
                        color={getStateColor(selectedOrder.state)}
                        size="medium"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  </Box>

                  {selectedOrder.invoice_name && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                        Comprobante
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'info.main' }}>
                        {selectedOrder.invoice_name}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Desglose de Impuestos */}
                  <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          Subtotal
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          S/ {selectedOrder.amount_untaxed.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          IGV (18%)
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          S/ {selectedOrder.amount_tax.toFixed(2)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Box sx={{ bgcolor: 'success.main', color: 'white', p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Total del Pedido
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      S/ {selectedOrder.amount_total.toFixed(2)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Columna Derecha - Productos */}
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}>
                  Productos ({selectedOrder.order_lines?.length || 0})
                </Typography>

                <Box sx={{ maxHeight: '400px', overflowY: 'auto', pr: 1 }}>
                  {selectedOrder.order_lines && selectedOrder.order_lines.map((line, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 2,
                        mb: 1.5,
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, flex: 1, mr: 2 }}>
                          {line.product_name}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.main' }}>
                          S/ {line.price_subtotal.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip
                          label={`${line.product_qty} ${line.uom_name}`}
                          size="small"
                          sx={{
                            borderColor: 'info.main',
                            color: 'info.main',
                            fontWeight: 600
                          }}
                          variant="outlined"
                        />
                        <Chip
                          label={`S/ ${line.price_unit.toFixed(2)} c/u`}
                          size="small"
                          sx={{
                            borderColor: 'text.secondary',
                            color: 'text.primary',
                          }}
                          variant="outlined"
                        />
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: '100%' }}>
          <Box sx={{ display: 'flex', gap: 1, flex: 1, width: { xs: '100%', sm: 'auto' } }}>
            {/* Botón Comprobante (PDF) */}
            <Tooltip title="Descargar PDF de Factura">
              <span style={{ flex: 1 }}>
                <Button
                  onClick={() => selectedOrder && handleDownloadPdf(selectedOrder.id)}
                  variant="contained"
                  color="primary"
                  startIcon={downloadingPdf ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}
                  disabled={downloadingPdf}
                  fullWidth
                  sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                >
                  {downloadingPdf ? 'Descargando...' : 'Comprobante'}
                </Button>
              </span>
            </Tooltip>

            {/* Botón Cancelar Pedido - solo si está confirmado */}
            {selectedOrder && selectedOrder.state === 'sale' && (
              <Tooltip title="Solicitar cancelación del pedido">
                <span style={{ flex: 1 }}>
                  <Button
                    onClick={() => setShowCancelModal(true)}
                    variant="contained"
                    color="error"
                    startIcon={<CancelIcon />}
                    fullWidth
                    sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                  >
                    Cancelar
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>

          {/* Botón Cerrar */}
          <Tooltip title="Cerrar">
            <IconButton
              onClick={handleCloseDialog}
              color="inherit"
              size="large"
              sx={{
                border: '2px solid',
                borderColor: 'grey.400',
                '&:hover': { bgcolor: 'action.selected' },
                alignSelf: { xs: 'center', sm: 'auto' }
              }}
            >
              <CloseIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmación de Cancelación */}
      <Dialog
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CancelIcon fontSize="large" />
          Confirmar Cancelación
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción es irreversible
          </Alert>
          <Typography variant="body1" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.125rem' } }}>
            ¿Está seguro de cancelar el pedido {selectedOrder?.name}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Se realizará automáticamente:
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 2 }}>
            • Nota de Crédito
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            • Devolución de stock
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            • Marcado como cancelado
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setShowCancelModal(false)}
            variant="outlined"
            disabled={cancellingOrder}
          >
            No, mantener pedido
          </Button>
          <Button
            onClick={handleCancelOrder}
            variant="contained"
            color="error"
            disabled={cancellingOrder}
            startIcon={cancellingOrder ? <CircularProgress size={20} color="inherit" /> : <CancelIcon />}
          >
            {cancellingOrder ? 'Cancelando...' : 'Sí, cancelar pedido'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SaleOrderList;